/**
 * shareTarget.js — client-side helper for Web Share Target (2026-01)
 *
 * Companion to /public/service-worker.js. When the OS-level "Share" sheet
 * hands us files via POST /share-target, the SW stores them in IndexedDB
 * under a random ID and redirects the tab to /upload?share=<id>. This
 * helper reads them back out.
 *
 * Usage (inside a React component / effect):
 *   import { consumeSharedFiles } from '../lib/shareTarget';
 *   useEffect(() => {
 *     consumeSharedFiles().then((files) => {
 *       if (files?.length) addFiles(files);
 *     });
 *   }, []);
 */

const SHARE_DB_NAME = 'emb-share-target';
const SHARE_STORE = 'files';

function openShareDB() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') { reject(new Error('indexedDB unavailable')); return; }
        const req = indexedDB.open(SHARE_DB_NAME, 1);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(SHARE_STORE)) {
                db.createObjectStore(SHARE_STORE, { keyPath: 'id' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function getAndDelete(id) {
    const db = await openShareDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(SHARE_STORE, 'readwrite');
        const store = tx.objectStore(SHARE_STORE);
        const req = store.get(id);
        req.onsuccess = () => {
            const val = req.result;
            if (val) store.delete(id);
            resolve(val || null);
        };
        req.onerror = () => reject(req.error);
    });
}

/**
 * Reads the ?share=<id> query param (if any), pulls the payload out of
 * IndexedDB and returns an array of real File objects ready to be added to
 * the upload queue. Also removes the ?share=<id> param from the URL so a
 * refresh doesn't try to consume the same payload twice (it's already been
 * deleted from IndexedDB above, but we clean the URL for UX polish).
 *
 * Returns [] when there's nothing to consume.
 */
export async function consumeSharedFiles() {
    try {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('share');
        if (!id) return [];
        if (id === 'error') {
            _cleanShareParam();
            return [];
        }
        const payload = await getAndDelete(id);
        _cleanShareParam();
        if (!payload || !Array.isArray(payload.files) || payload.files.length === 0) return [];
        const files = payload.files.map((f) => {
            const blob = new Blob([f.buffer], { type: f.type || 'application/octet-stream' });
            return new File([blob], f.name || 'shared-file', { type: f.type || 'application/octet-stream' });
        });
        return files;
    } catch (err) {
        // Silent — Web Share Target is a progressive enhancement.
        // eslint-disable-next-line no-console
        console.warn('[shareTarget] Failed to consume shared payload:', err);
        return [];
    }
}

function _cleanShareParam() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('share');
        window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }
}
